import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Landmark } from 'lucide-react'

import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { DetailList, DetailRow } from '@/components/DetailList'
import type { Advertiser } from '@/services/advertisers/advertisersApi'
import type { MoneyAmount } from '@/services/networks/networksApi'
import { ElectionsApi } from '@/services/political/electionsApi'
import { ENTITY_TYPES } from '@/utils/advertiserPolitical'
import { formatNumber } from '@/utils/format'
import { ELECTIONS_QUERY_KEY } from './ElectionSelect'
import { entityLabelKey, legalEntityKey } from './AdvertiserEntityFields'

/** Election names run long, so the labels get more room than the default. */
const ROW_LAYOUT = 'sm:grid-cols-[16rem_1fr]'

function formatMoney(money: MoneyAmount | null): string {
  return money ? formatNumber(money.amount, { style: 'currency', currency: money.currency }) : '—'
}

/**
 * Read-only view of the political budgets and entities an advertiser declares
 * itself, without a linked political campaign.
 */
export function AdvertiserPoliticalSummaryCard({ advertiser }: { advertiser: Advertiser }) {
  const { t } = useTranslation()
  const { data: elections = [] } = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
  })
  const electionName = (id: string) => elections.find((e) => e.id === id)?.name || id

  return (
    <Card tone="attention">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Landmark} />
          {t('advertisers.politicalDetails')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DetailList>
          {advertiser.politicalBudgets.map((budget) => (
            <DetailRow key={budget.electionId} label={electionName(budget.electionId)} className={ROW_LAYOUT}>
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {t('political.totalBudget')}: {formatMoney(budget.totalBudget)}
                </span>
                {budget.topics.length === 0 ? (
                  <span className="text-muted-foreground">{t('advertisers.noTopics')}</span>
                ) : (
                  budget.topics.map((topic) => (
                    <span key={topic} className="text-muted-foreground">
                      {topic}: {formatMoney(budget.topicBudgets[topic])}
                    </span>
                  ))
                )}
              </span>
            </DetailRow>
          ))}
          {ENTITY_TYPES.map((type) => {
            const entity = advertiser.entities.find((e) => e.type === type)
            const legalKey = entity?.legalEntity ? legalEntityKey(entity.legalEntity) : undefined
            return (
              <DetailRow key={type} label={t(entityLabelKey(type))} className={ROW_LAYOUT}>
                {entity ? (
                  <span className="flex flex-col gap-0.5">
                    <span className="font-medium">{entity.legalName || '—'}</span>
                    {legalKey && <span className="text-muted-foreground">{t(legalKey)}</span>}
                    {entity.contactEmail && <span className="text-muted-foreground">{entity.contactEmail}</span>}
                    {entity.url && <span className="text-muted-foreground">{entity.url}</span>}
                    {entity.vatId && (
                      <span className="text-muted-foreground">
                        {t('political.vatId')}: {entity.vatId}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="text-muted-foreground">{t('advertisers.entityIsTheAdvertiser')}</span>
                )}
              </DetailRow>
            )
          })}
        </DetailList>
      </CardContent>
    </Card>
  )
}
